import React, { useCallback, useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Form } from 'react-final-form';
import { Button } from 'ncoded-component-library';
import RfiCreatedSuccessScreen from './components/RfiCreatedSuccessScreen';
import { Link, useParams } from 'react-router-dom';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import utils, { getArrayOfEmails, getCheckedValuesInObject } from 'utils';
import { getIsStartOfDayToday } from 'dateUtils';
import RfiContext from 'router/subrouters/DataRoom/providers/Rfi/Rfi.context';
import useCreateRfi from '../../hooks/useCreateRfi';

import './RfiCreate.styles.scss';
import './RfiCreate.styles.responsive.scss';

type RfiCreateProps = {
  className?: string;
};

const RfiCreate: React.FC<RfiCreateProps> = (props) => {
  const { className } = props;

  const [showSuccess, setShowSuccess] = useState(false);
  const [rfiId, setRfiId] = useState('');

  const { popError, popServerError } = useContext(PopNotificationsContext);
  const {
    dataRoom: { _id },
  } = useContext(DataRoomContext);
  const { rfiTemplates, refetch, refetchTemplates, addRfi, getRfiById } =
    useContext(RfiContext);

  const rfi = useMemo(() => getRfiById(rfiId), [rfiId, getRfiById]);

  const { t } = useTranslation();

  const classes = classNames('rfi-create', 'white-panel', className);

  const { templateId } = useParams<{ templateId: string }>();

  const template = rfiTemplates.find(({ _id }) => _id === templateId);

  const { initialValues, RenderSteps } = useCreateRfi(false, template);

  const handleOnSubmit = useCallback(
    async (values) => {
      const {
        documents,
        customDocuments,
        notificationMedium,
        recipients,
        recipientsUnsubmited,
        dueDate,
        dueTime,
        saveAsTemplate,
        saveAsTemplateName,
        ...restValues
      } = values;

      const documentsFinal = [...documents, ...customDocuments]
        .filter(({ checked }) => checked)
        .map(({ name, period }) => ({ name, period }));

      if (!documentsFinal.length) {
        popError(t('chooseAtLeastOneDocument'));
        return;
      }

      const notificationMediumFinal =
        getCheckedValuesInObject(notificationMedium);

      const finalEmails = getArrayOfEmails(recipients, recipientsUnsubmited);

      const finalDate =
        dueDate && dueTime
          ? utils.getDateInUserTimezone(dueDate.toISOString(), +dueTime)
          : dueDate
          ? dueDate.toISOString()
          : undefined;

      const data = {
        documents: documentsFinal,
        notifyOnComplete: !!notificationMediumFinal.length,
        notificationMedium: notificationMediumFinal,
        recipients: finalEmails,
        dueDate: finalDate,
        saveAsTemplate,
        ...(saveAsTemplate && { saveAsTemplateName }),
        ...restValues,
      };

      try {
        const { data: rfi } = await dataRoomApi.rfi.createRfi(_id, data);

        setRfiId(rfi._id);
        addRfi(rfi);
        setShowSuccess(true);
        refetch();

        if (saveAsTemplate) {
          refetchTemplates();
        }
      } catch (e) {
        popServerError(e);
      }
    },
    [_id, popError, t, addRfi, refetch, refetchTemplates, popServerError],
  );

  const validate = (values: any) => {
    const errors: Record<string, string> = {};

    const { dueDate, dueTime } = values;

    if (getIsStartOfDayToday(dueDate) && !dueTime) {
      errors.dueTime = t('requiredField');
    }

    return errors;
  };

  return (
    <div className={classes}>
      {showSuccess ? (
        <RfiCreatedSuccessScreen
          rfi={rfi}
          onCreateNew={() => setShowSuccess(false)}
        />
      ) : (
        <>
          <div className="panel-title">
            <h3>{t('createNewRequest')}</h3>
            <Link to="../list" className="ncoded-button ncoded-button--outline">
              {t('showAll')}
            </Link>
          </div>

          <Form
            onSubmit={handleOnSubmit}
            initialValues={initialValues}
            validate={validate}
            render={({ handleSubmit, submitting }) => (
              <form onSubmit={handleSubmit}>
                {RenderSteps}
                <div className="actions">
                  <Button type="submit" disabled={submitting}>
                    {t('createRequest')}
                  </Button>
                </div>
              </form>
            )}
          />
        </>
      )}
    </div>
  );
};

export default RfiCreate;
